import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { Z_INDEX } from '../../utils/constants/z-index.constant';
import {
  clearConversation,
  closeAssistant,
  toggleAssistant,
} from '../../features/assistant/slices/assistant.slice';
import {
  checkAssistantAvailability,
  resetAssistantConversation,
  restoreAssistantConversation,
  retryAssistantMessage,
  sendAssistantMessage,
  stopAssistantResponse,
} from '../../features/assistant/thunks/assistant.thunk';
import brandIcon from '../../assets/images/logos/ba-brand-icon-colored.png';
import AssistantLauncher from './AssistantLauncher';
import AssistantMessageBubble from './AssistantMessageBubble';

const BRAND_GRADIENT = 'linear-gradient(135deg, #E55325 0%, #F9A825 100%)';
const HEADING_FONT = "'Bricolage Grotesque', sans-serif";
const BODY_FONT = "'Poppins', sans-serif";

/** Shown on an empty conversation to give people an obvious first move. */
const SUGGESTIONS = [
  'How do I sell on BaoAfrik?',
  'Find me African spices',
  'How do requests work?',
];

const MAX_MESSAGE_LENGTH = 2000;

const TypingDots: React.FC = () => (
  <div className="flex items-center gap-1" aria-hidden>
    {[0, 1, 2].map((i) => (
      <motion.span
        key={i}
        className="h-1.5 w-1.5 rounded-full bg-[#6A6A6A]"
        animate={{ opacity: [0.25, 1, 0.25] }}
        transition={{ duration: 1.1, repeat: Infinity, delay: i * 0.18 }}
      />
    ))}
  </div>
);

const AssistantWidget: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { isOpen, isEnabled, messages, isStreaming, toolStatus, unreadCount } =
    useAppSelector((state) => state.assistant);

  const [draft, setDraft] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const isPinnedToBottom = useRef(true);

  // Re-probes whenever availability is unknown — including after a logout, which
  // resets the whole store and would otherwise leave the launcher hidden until reload.
  useEffect(() => {
    if (isEnabled === null) dispatch(checkAssistantAvailability());
  }, [dispatch, isEnabled]);

  // Replays the server-side conversation so a reload does not appear to lose the chat.
  useEffect(() => {
    if (isOpen) dispatch(restoreAssistantConversation());
  }, [dispatch, isOpen]);

  /**
   * Follows the stream, but only while the user is already at the bottom — scrolling
   * up to re-read something must not be yanked back by the next token.
   */
  useLayoutEffect(() => {
    const node = scrollRef.current;
    if (node && isPinnedToBottom.current) node.scrollTop = node.scrollHeight;
  }, [messages, toolStatus, isOpen]);

  const handleScroll = useCallback(() => {
    const node = scrollRef.current;
    if (!node) return;
    const distanceFromBottom = node.scrollHeight - node.scrollTop - node.clientHeight;
    isPinnedToBottom.current = distanceFromBottom < 60;
  }, []);

  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') dispatch(closeAssistant());
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, dispatch]);

  const autoGrow = useCallback(() => {
    const node = inputRef.current;
    if (!node) return;
    node.style.height = 'auto';
    node.style.height = `${Math.min(node.scrollHeight, 112)}px`;
  }, []);

  const handleSend = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isStreaming) return;
      isPinnedToBottom.current = true;
      setDraft('');
      requestAnimationFrame(autoGrow);
      dispatch(sendAssistantMessage({ message: trimmed, currentPath: location.pathname }));
    },
    [dispatch, isStreaming, location.pathname, autoGrow],
  );

  const handleReset = useCallback(() => {
    stopAssistantResponse();
    dispatch(resetAssistantConversation());
    dispatch(clearConversation());
  }, [dispatch]);

  const handleNavigate = useCallback(() => dispatch(closeAssistant()), [dispatch]);

  const lastMessage = messages[messages.length - 1];
  const canRetry = !isStreaming && Boolean(lastMessage?.isError);

  // The status probe hasn't answered yet, or the backend has no API key configured.
  if (!isEnabled) return null;

  const panel = (
    <AnimatePresence>
      {isOpen ? (
        <motion.div
          key="assistant-panel"
          role="dialog"
          aria-label="BaoAfrik assistant"
          initial={{ opacity: 0, y: 16, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 12, scale: 0.98 }}
          transition={{ type: 'spring', stiffness: 400, damping: 32 }}
          className="fixed inset-0 flex flex-col overflow-hidden bg-white sm:inset-auto sm:bottom-24 sm:right-6 sm:h-[min(620px,calc(100vh-9rem))] sm:w-[390px] sm:rounded-2xl sm:shadow-2xl"
          style={{
            zIndex: Z_INDEX.TOOLTIP,
            fontFamily: BODY_FONT,
            boxShadow: '0 24px 60px rgba(33, 33, 33, 0.18)',
          }}
        >
          <header
            className="flex shrink-0 items-center gap-3 px-4 py-3 text-white"
            style={{ background: BRAND_GRADIENT }}
          >
            <div
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white"
              style={{
                boxShadow:
                  'inset 0 1px 2px rgba(255,255,255,0.9), 0 2px 5px rgba(120, 53, 15, 0.28)',
              }}
            >
              <img src={brandIcon} alt="" aria-hidden className="h-5 w-5 object-contain" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[15px] font-semibold leading-tight" style={{ fontFamily: HEADING_FONT }}>
                Bao Assistant
              </p>
              <p className="truncate text-xs text-white/85">
                {isStreaming ? (toolStatus ?? 'Typing…') : 'Here to help you around BaoAfrik'}
              </p>
            </div>

            {messages.length ? (
              <button
                type="button"
                onClick={handleReset}
                aria-label="Start a new conversation"
                title="Start a new conversation"
                className="rounded-full p-1.5 transition-colors hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path
                    d="M4 4v6h6M20 20v-6h-6M20 9A8 8 0 0 0 5.6 6.6M4 15a8 8 0 0 0 14.4 2.4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            ) : null}

            <button
              type="button"
              onClick={() => dispatch(closeAssistant())}
              aria-label="Close assistant"
              className="rounded-full p-1.5 transition-colors hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </header>

          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain bg-gray-50 px-4 py-4"
          >
            {messages.length === 0 ? (
              <div className="space-y-3">
                <p className="text-sm leading-relaxed text-[#6A6A6A]">
                  Hi! I'm Bao. Ask me how BaoAfrik works, and I can help you find listings,
                  post a request, or get to the right page.
                </p>
                <div className="flex flex-col gap-2">
                  {SUGGESTIONS.map((suggestion) => (
                    <button
                      key={suggestion}
                      type="button"
                      onClick={() => handleSend(suggestion)}
                      className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-left text-sm text-[#212121] transition-colors hover:border-[#E55325]/50 hover:bg-[#E55325]/[0.04] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E55325]/40"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((message) => (
                <AssistantMessageBubble
                  key={message.id}
                  message={message}
                  onNavigate={handleNavigate}
                />
              ))
            )}

            {/* Announced separately so screen readers get the finished reply rather
                than every streamed token. */}
            <p className="sr-only" aria-live="polite">
              {isStreaming
                ? (toolStatus ?? 'Bao is replying')
                : lastMessage?.role === 'assistant'
                  ? lastMessage.text
                  : ''}
            </p>

            {isStreaming && !lastMessage?.text ? (
              <div className="flex items-center gap-2 pl-1">
                <TypingDots />
                {toolStatus ? (
                  <span className="text-xs italic text-[#6A6A6A]">{toolStatus}</span>
                ) : null}
              </div>
            ) : null}

            {canRetry ? (
              <button
                type="button"
                onClick={() => dispatch(retryAssistantMessage(location.pathname))}
                className="rounded-full border border-gray-300 px-3 py-1.5 text-xs font-semibold text-[#212121] transition-colors hover:border-[#E55325] hover:text-[#E55325] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E55325]/40"
              >
                Try again
              </button>
            ) : null}
          </div>

          <form
            className="flex shrink-0 items-end gap-2 border-t border-gray-200 bg-white px-3 py-3"
            style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
            onSubmit={(event) => {
              event.preventDefault();
              handleSend(draft);
            }}
          >
            <textarea
              ref={inputRef}
              value={draft}
              onChange={(event) => {
                setDraft(event.target.value);
                autoGrow();
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  handleSend(draft);
                }
              }}
              rows={1}
              maxLength={MAX_MESSAGE_LENGTH}
              placeholder="Ask me anything about BaoAfrik…"
              aria-label="Message the assistant"
              className="max-h-28 flex-1 resize-none rounded-xl border border-gray-200 px-3 py-2.5 text-sm text-[#212121] placeholder:text-gray-400 focus:border-[#E55325] focus:outline-none"
            />

            {isStreaming ? (
              <button
                type="button"
                onClick={stopAssistantResponse}
                aria-label="Stop replying"
                title="Stop"
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gray-300 text-[#6A6A6A] transition-colors hover:border-[#E55325] hover:text-[#E55325] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E55325]/40"
              >
                <span className="block h-3 w-3 rounded-[2px] bg-current" aria-hidden />
              </button>
            ) : (
              <button
                type="submit"
                disabled={!draft.trim()}
                aria-label="Send message"
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E55325]/40"
                style={{ background: BRAND_GRADIENT }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path d="M4 12l16-8-6 8 6 8-16-8z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                </svg>
              </button>
            )}
          </form>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );

  // Portalled so page stacking contexts can never clip or bury the panel.
  return createPortal(
    <>
      {panel}
      <AssistantLauncher
        isOpen={isOpen}
        unreadCount={unreadCount}
        pathname={location.pathname}
        onClick={() => dispatch(toggleAssistant())}
        className={isOpen ? 'hidden sm:flex' : ''}
      />
    </>,
    document.body,
  );
};

export default AssistantWidget;
